import React, { useRef, useEffect } from 'react';
import { Box, IconButton, Button, Slide, Typography } from '@mui/material';
import {
    KeyboardArrowUp,
    KeyboardArrowDown,
    KeyboardArrowLeft,
    KeyboardArrowRight,
    Add as AddIcon,
    Remove as RemoveIcon,
    Close as CloseIcon,
} from '@mui/icons-material';

const hoursRow1 = [1, 2, 3, 4, 5, 6, 7, 8];
const hoursRow2 = [9, 10, 11, 12, 13, 14, 15, 16];
const hoursRow3 = [17, 18, 19, 20, 21, 22, 23, 24];

export default function TimesheetKeyboard({
    isOpen,
    value,
    employeeName,
    jobName,
    dayTotal,
    onValueChange,
    onSubmit,
    onCancel,
    onNavigate,
    onDelete,
    onHeightChange,
}) {
    const kbRef = useRef(null);
    useEffect(() => {
        if (isOpen && kbRef.current && onHeightChange) {
            onHeightChange(kbRef.current.offsetHeight);
        }
        if (!isOpen && onHeightChange) {
            onHeightChange(0);
        }
    }, [isOpen, onHeightChange]);

    const currentValue = parseFloat(value) || 0;

    const handleSetHours = (hours) => {
        if (hours >= 0 && hours <= 24) {
            onValueChange(hours.toString());
        }
    };

    const handleAddHalf = () => {
        const newValue = Math.min(24, currentValue + 0.5);
        onValueChange(newValue.toString());
    };

    const handleSubtractHalf = () => {
        const newValue = Math.max(0, currentValue - 0.5);
        onValueChange(newValue.toString());
    };

    const navBtnSx = {
        bgcolor: 'grey.200',
        '&:hover': { bgcolor: 'grey.300' },
        borderRadius: 2,
        color: 'text.primary',
    };

    const hourBtnSx = (h) => {
        const isExactMatch = value === h.toString();
        const isSelected = currentValue === h || (currentValue > h - 0.5 && currentValue < h + 0.5 && Math.floor(currentValue) === h);

        if (isExactMatch) {
            return {
                bgcolor: 'primary.main',
                color: 'white',
                fontWeight: 700,
                boxShadow: '0 2px 8px rgba(25, 118, 210, 0.3)',
                '&:hover': { bgcolor: 'primary.dark' },
            };
        }
        if (isSelected) {
            return {
                bgcolor: 'primary.50',
                color: 'primary.main',
                fontWeight: 700,
                border: '2px solid',
                borderColor: 'primary.main',
                '&:hover': { bgcolor: 'primary.100' },
            };
        }
        return {
            bgcolor: 'grey.100',
            color: 'text.primary',
            fontWeight: 600,
            '&:hover': { bgcolor: 'grey.200' },
        };
    };

    return (
        <Slide direction="up" in={isOpen} mountOnEnter unmountOnExit>
            <Box
                ref={kbRef}
                sx={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: 1300,
                    bgcolor: 'background.paper',
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    boxShadow: '0 -4px 20px rgba(0,0,0,0.1)',
                }}
            >
                {/* Close button */}
                <IconButton
                    onClick={onCancel}
                    size="small"
                    sx={{
                        position: 'absolute',
                        right: 12,
                        top: -20,
                        bgcolor: 'background.paper',
                        border: '1px solid',
                        borderColor: 'divider',
                        boxShadow: 1,
                        width: 32,
                        height: 32,
                        zIndex: 10,
                        '&:hover': { bgcolor: 'grey.100' },
                    }}
                >
                    <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>

                {/* Main content */}
                <Box sx={{ display: 'flex', p: 1.5, gap: 1.5 }}>
                    {/* LEFT: Navigation */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        {/* Up */}
                        <IconButton onClick={() => onNavigate('up')} sx={{ ...navBtnSx, width: 72, height: 44 }}>
                            <KeyboardArrowUp />
                        </IconButton>
                        {/* Left + Right */}
                        <Box sx={{ display: 'flex', gap: 0.75 }}>
                            <IconButton onClick={() => onNavigate('left')} sx={{ ...navBtnSx, width: 34, height: 44 }}>
                                <KeyboardArrowLeft sx={{ fontSize: 20 }} />
                            </IconButton>
                            <IconButton onClick={() => onNavigate('right')} sx={{ ...navBtnSx, width: 34, height: 44 }}>
                                <KeyboardArrowRight sx={{ fontSize: 20 }} />
                            </IconButton>
                        </Box>
                        {/* Down */}
                        <IconButton onClick={() => onNavigate('down')} sx={{ ...navBtnSx, width: 72, height: 44 }}>
                            <KeyboardArrowDown />
                        </IconButton>
                    </Box>

                    {/* CENTER: Hours grid */}
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        {[hoursRow1, hoursRow2, hoursRow3].map((row, rowIdx) => (
                            <Box key={rowIdx} sx={{ display: 'flex', gap: 0.75, height: 44 }}>
                                {row.map((h) => (
                                    <Button
                                        key={h}
                                        onClick={() => handleSetHours(h)}
                                        variant="text"
                                        sx={{
                                            flex: 1,
                                            minWidth: 0,
                                            borderRadius: 2,
                                            fontSize: '0.875rem',
                                            p: 0,
                                            ...hourBtnSx(h),
                                        }}
                                    >
                                        {h}
                                    </Button>
                                ))}
                            </Box>
                        ))}
                    </Box>

                    {/* RIGHT: Half hours & clear */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                        {/* +0.5 */}
                        <Button
                            onClick={handleAddHalf}
                            variant="contained"
                            color="success"
                            sx={{
                                width: 64,
                                height: 44,
                                borderRadius: 2,
                                fontWeight: 700,
                                fontSize: '0.8rem',
                                minWidth: 0,
                            }}
                            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                        >
                            .5
                        </Button>
                        {/* -0.5 */}
                        <Button
                            onClick={handleSubtractHalf}
                            variant="contained"
                            color="error"
                            sx={{
                                width: 64,
                                height: 44,
                                borderRadius: 2,
                                fontWeight: 700,
                                fontSize: '0.8rem',
                                minWidth: 0,
                            }}
                            startIcon={<RemoveIcon sx={{ fontSize: 16 }} />}
                        >
                            .5
                        </Button>
                        {/* 0 — нажатие сбрасывает часы в 0 (запись будет удалена при сохранении) */}
                        <Button
                            onClick={() => handleSetHours(0)}
                            variant="text"
                            sx={{
                                width: 64,
                                height: 44,
                                borderRadius: 2,
                                minWidth: 0,
                                fontSize: '1rem',
                                fontWeight: 700,
                                ...hourBtnSx(0),
                            }}
                        >
                            0
                        </Button>
                    </Box>
                </Box>
            </Box>
        </Slide>
    );
}
